import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail, Clock, Calendar } from 'lucide-react';
import Button from '../ui/Button';
import { useAuth } from '../../contexts/AppContext';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuth();
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  const navigation = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Services', href: '/services' },
    { name: 'Doctors', href: '/doctors' },
    { name: 'Contact', href: '/contact' },
  ];

  const handleLogout = () => {
    logout();
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-white/95 backdrop-blur-md shadow-lg sticky top-0 z-50 border-b border-gray-100">
      {/* Main header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-4">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-500 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-xl">C</span>
              </div>
              <div>
                                        <h1 className="text-2xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
                          ClinicIT
                        </h1>
                        <p className="text-sm text-gray-500 font-medium">Medical Center</p>
              </div>
            </Link>
              </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-gray-700 hover:text-teal-600 px-3 py-2 font-medium transition-colors ${
                isActive('/') ? 'text-teal-600' : ''
              }`}
            >
              Home
            </Link>
            <Link
              to="/services"
              className={`text-gray-700 hover:text-teal-600 px-3 py-2 font-medium transition-colors ${
                isActive('/services') ? 'text-teal-600' : ''
              }`}
            >
              Services
            </Link>
            <Link
              to="/about"
              className={`text-gray-700 hover:text-teal-600 px-3 py-2 font-medium transition-colors ${
                isActive('/about') ? 'text-teal-600' : ''
              }`}
            >
              About
            </Link>
            <Link
              to="/doctors"
              className={`text-gray-700 hover:text-teal-600 px-3 py-2 font-medium transition-colors ${
                isActive('/doctors') ? 'text-teal-600' : ''
              }`}
            >
              Doctors
            </Link>
            <Link
              to="/contact"
              className={`text-gray-700 hover:text-teal-600 px-3 py-2 font-medium transition-colors ${
                isActive('/contact') ? 'text-teal-600' : ''
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Auth and CTA */}
          <div className="hidden lg:flex items-center space-x-4">
              {isAuthenticated ? (
                <div className="flex items-center space-x-4">
                <span className="text-sm text-gray-600">Hello, {user?.firstName}</span>
                  {user?.role === 'admin' && (
                    <Link
                      to="/admin"
                    className="text-sm text-gray-600 hover:text-teal-600 transition-colors"
                    >
                      Admin Dashboard
                    </Link>
                  )}
                  {user?.role === 'patient' && (
                    <Link
                      to="/patient"
                    className="text-sm text-gray-600 hover:text-teal-600 transition-colors"
                    >
                      Patient Portal
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                  className="text-sm text-gray-600 hover:text-teal-600 transition-colors"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                className="text-gray-600 hover:text-teal-600 transition-colors font-medium"
              >
                Login
              </Link>
            )}

            <Link to="/booking">
              <Button className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold px-6 py-2 rounded-full shadow-lg">
                Book Appointment
              </Button>
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-700 hover:text-teal-600 p-2"
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className="lg:hidden bg-white/95 backdrop-blur-md border-t border-gray-200">
          <div className="px-4 py-4 space-y-3">
            <Link
              to="/"
              className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                isActive('/') ? 'text-teal-600 bg-teal-50' : 'text-gray-700 hover:text-teal-600 hover:bg-gray-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Home
            </Link>
            <Link
              to="/services"
              className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                isActive('/services') ? 'text-teal-600 bg-teal-50' : 'text-gray-700 hover:text-teal-600 hover:bg-gray-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Services
            </Link>
            <Link
              to="/about"
              className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                isActive('/about') ? 'text-teal-600 bg-teal-50' : 'text-gray-700 hover:text-teal-600 hover:bg-gray-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              About
            </Link>
            <Link
              to="/doctors"
              className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                isActive('/doctors') ? 'text-teal-600 bg-teal-50' : 'text-gray-700 hover:text-teal-600 hover:bg-gray-50'
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              Doctors
            </Link>
              <Link
              to="/contact"
              className={`block px-3 py-2 text-base font-medium rounded-lg transition-colors ${
                isActive('/contact') ? 'text-teal-600 bg-teal-50' : 'text-gray-700 hover:text-teal-600 hover:bg-gray-50'
                }`}
                onClick={() => setIsMenuOpen(false)}
              >
              Contact
            </Link>
            
            <div className="pt-4 border-t border-gray-200 space-y-3">
              {isAuthenticated ? (
                <div className="space-y-2">
                  <span className="block px-3 py-2 text-sm text-gray-600">Hello, {user?.firstName}</span>
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-3 py-2 text-sm text-gray-600 hover:text-teal-600"
                  >
                    Logout
                  </button>
                </div>
              ) : (
                <Link 
                  to="/login" 
                  onClick={() => setIsMenuOpen(false)}
                  className="block px-3 py-2 text-gray-600 hover:text-teal-600 font-medium"
                >
                  Login
              </Link>
              )}
              
              <Link to="/booking" onClick={() => setIsMenuOpen(false)}>
                <Button className="w-full bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-white font-semibold py-3 rounded-full">
                  Book Appointment
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;